// =============================================================
// Dashboard — Firebase-powered
// =============================================================
// All persistent data is in Firestore. localStorage is no longer used.
//   • Profile (belt, celebrated, displayName) → /users/{uid}
//   • Journal entries                          → /users/{uid}/notes
//   • Calendar events                          → /users/{uid}/events
//   • Custom skills                            → /users/{uid}/customSkills
//   • Tournament photos + docs are handled inside TournamentSection
//
// Email reminders:
//   • Immediate "Email Me This Reminder" → calls Cloud Function `sendReminderEmail`
//   • Scheduled reminders → written to /scheduledReminders, processed by the
//     `processScheduledReminders` Cloud Function cron (every 60s)
// =============================================================

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  Bell,
  NotebookPen,
  Trophy,
  Search,
  Trash2,
  Dumbbell,
  Target,
  LogOut,
  Shield,
  Mail,
  Square,
} from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { useAuth } from "../context/AuthContext";
import { useUserCollection, useUserProfile } from "../hooks/useFirestore";
import { db, firebaseApp } from "../lib/firebase";
import PromotionCard from "../components/PromotionCard";
import ActivityHeatmap from "../components/ActivityHeatmap";
import TournamentSection from "../components/TournamentSection";
import SkillRoadmap from "../components/SkillRoadmap";
import NoteForm from "../components/NoteForm";
import EventForm from "../components/EventForm";
import { TooltipProvider } from "../components/ui/tooltip";
import { DEFAULT_SKILLS } from "../data/skills";

const functions = getFunctions(firebaseApp);
const sendReminderEmailFn = httpsCallable(functions, "sendReminderEmail");

const BELTS = [
  { key: "White", bg: "bg-zinc-50", text: "text-zinc-950", accent: "border-t-zinc-200", fill: "#F8FAFC" },
  { key: "Blue", bg: "bg-blue-700", text: "text-white", accent: "border-t-blue-700", fill: "#1D4ED8" },
  { key: "Purple", bg: "bg-purple-800", text: "text-white", accent: "border-t-purple-800", fill: "#6B21A8" },
  { key: "Brown", bg: "bg-amber-900", text: "text-white", accent: "border-t-amber-900", fill: "#78350F" },
  { key: "Black", bg: "bg-black", text: "text-white", accent: "border-t-zinc-900", fill: "#000000" },
];

const HERO_INITIAL = { opacity: 0, y: 14 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.45 };
const ITEM_INITIAL = { opacity: 0, y: 6 };
const ITEM_ANIMATE = { opacity: 1, y: 0 };

const ALERT_OFFSET_MINUTES = {
  "10 min before": 10,
  "30 min before": 30,
  "1 hour before": 60,
  "1 day before": 1440,
};

function getBelt(key) {
  return BELTS.find((b) => b.key === key) || BELTS[0];
}

function fireConfetti() {
  const colors = ["#DC2626", "#F8FAFC", "#1D4ED8", "#6B21A8", "#78350F"];
  const end = Date.now() + 1500;
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 65, origin: { x: 0, y: 0.7 }, colors, gravity: 0.9 });
    confetti({ particleCount: 4, angle: 120, spread: 65, origin: { x: 1, y: 0.7 }, colors, gravity: 0.9 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function computeFireAt(date, time, alert) {
  try {
    const dt = new Date(`${date}T${time}:00`);
    if (isNaN(dt.getTime())) return null;
    const offset = ALERT_OFFSET_MINUTES[alert] ?? 30;
    return new Date(dt.getTime() - offset * 60 * 1000);
  } catch {
    return null;
  }
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const uid = user?.uid;

  const { profile, set: setProfile } = useUserProfile(uid);
  const notesCol = useUserCollection(uid, "notes");
  const eventsCol = useUserCollection(uid, "events");
  const customSkillsCol = useUserCollection(uid, "customSkills", "createdAt", "asc");

  const notes = notesCol.items;
  const events = eventsCol.items;
  const customSkills = customSkillsCol.items;

  const belt = profile?.belt || "White";
  const celebrated = profile?.celebrated || false;

  const [query_, setQuery] = useState("");
  const [newNote, setNewNote] = useState({ title: "", details: "", skill: "", rating: 3 });
  const [newEvent, setNewEvent] = useState({ date: "", time: "", title: "", alert: "30 min before" });
  const [sendingId, setSendingId] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);

  const currentBelt = getBelt(belt);

  const allSkills = useMemo(
    () => [...DEFAULT_SKILLS, ...customSkills.map((s) => ({ ...s, custom: true }))],
    [customSkills]
  );

  const filteredNotes = useMemo(() => {
    const q = query_.toLowerCase();
    return notes.filter(
      (n) =>
        (n.title || "").toLowerCase().includes(q) ||
        (n.details || "").toLowerCase().includes(q) ||
        (n.skill || "").toLowerCase().includes(q)
    );
  }, [notes, query_]);

  const noteSkillSet = useMemo(
    () => new Set(notes.map((n) => n.skill).filter(Boolean)),
    [notes]
  );
  const completedSkills = useMemo(
    () => allSkills.filter((s) => noteSkillSet.has(s.name)).length,
    [allSkills, noteSkillSet]
  );
  const progress = allSkills.length === 0 ? 0 : Math.min(100, Math.round((completedSkills / allSkills.length) * 100));
  const loggedSkillNames = useMemo(
    () => allSkills.filter((s) => noteSkillSet.has(s.name)).map((s) => s.name),
    [allSkills, noteSkillSet]
  );

  // Sync scheduled reminders to Firestore whenever events change.
  useEffect(() => {
    if (!uid) return;
    const handle = setTimeout(async () => {
      try {
        // Delete any existing scheduled reminders for this user not in current event list
        const existingQ = query(collection(db, "scheduledReminders"), where("userId", "==", uid));
        const existing = await getDocs(existingQ);
        const currentIds = new Set(events.map((e) => e.id));
        for (const d of existing.docs) {
          if (!currentIds.has(d.data().eventId)) {
            await deleteDoc(doc(db, "scheduledReminders", d.id));
          }
        }
        // Upsert current events
        for (const e of events) {
          const fireAt = computeFireAt(e.date, e.time, e.alert);
          if (!fireAt) continue;
          const alreadyPast = fireAt < new Date(Date.now() - 60 * 60 * 1000);
          await setDoc(doc(db, "scheduledReminders", `${uid}_${e.id}`), {
            userId: uid,
            eventId: e.id,
            title: e.title,
            date: e.date,
            time: e.time,
            alert: e.alert,
            belt,
            email: user?.email,
            displayName: user?.displayName || user?.email?.split("@")[0] || "Grappler",
            fireAt: Timestamp.fromDate(fireAt),
            fired: alreadyPast,
            createdAt: serverTimestamp(),
          });
        }
      } catch (err) {
        console.error("Reminder sync failed", err);
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [events, uid, belt, user?.email, user?.displayName]);

  useEffect(() => {
    if (progress === 100 && !celebrated && profile) {
      fireConfetti();
      setPromoOpen(true);
      setProfile({ celebrated: true });
    }
  }, [progress, celebrated, profile, setProfile]);

  async function addNote() {
    if (!newNote.title.trim() || !newNote.details.trim()) {
      toast.error("Title and details required");
      return;
    }
    try {
      await notesCol.add({
        ...newNote,
        date: new Date().toISOString().slice(0, 10),
      });
      setNewNote({ title: "", details: "", skill: "", rating: 3 });
      toast.success("Entry logged");
    } catch (err) {
      console.error("addNote failed", err);
      toast.error(err?.code === "permission-denied"
        ? "Firestore rules not published yet — see FIREBASE_SETUP.md step 2"
        : `Save failed: ${err?.message || "unknown error"}`);
    }
  }

  async function addEvent() {
    if (!newEvent.date || !newEvent.time || !newEvent.title.trim()) {
      toast.error("Date, time and title required");
      return;
    }
    try {
      await eventsCol.add({ ...newEvent });
      setNewEvent({ date: "", time: "", title: "", alert: "30 min before" });
      toast.success("Event scheduled");
    } catch (err) {
      console.error("addEvent failed", err);
      toast.error(err?.code === "permission-denied"
        ? "Firestore rules not published yet — see FIREBASE_SETUP.md step 2"
        : `Save failed: ${err?.message || "unknown error"}`);
    }
  }

  async function sendReminder(ev) {
    setSendingId(ev.id);
    try {
      await sendReminderEmailFn({
        event_title: ev.title,
        event_date: ev.date,
        event_time: ev.time,
        alert: ev.alert,
        belt,
      });
      toast.success(`Reminder sent to ${user?.email}`);
    } catch (err) {
      const msg = err?.message || "Failed to send reminder";
      toast.error(msg);
      console.error("sendReminder failed", err);
    } finally {
      setSendingId(null);
    }
  }

  return (
    <TooltipProvider>
      <div className="relative min-h-screen text-white">
        {/* Top nav */}
        <header className="relative z-10 border-b border-zinc-800 bg-[#0A0A0A]/80 backdrop-blur">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-600 flex items-center justify-center">
                <Shield className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-heading text-lg leading-none">TATAMI LOG</p>
                <p className="font-mono text-[10px] text-zinc-500 mt-1">TRAINING // INTEL // ROADMAP</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user?.photoURL && <img src={user.photoURL} alt="" className="w-8 h-8 border border-zinc-700" />}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold leading-tight" data-testid="user-name">{user?.displayName || user?.email?.split("@")[0]}</p>
                <p className="text-xs text-zinc-500 font-mono">{user?.email}</p>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="border border-zinc-700 hover:border-red-600 hover:text-red-500 px-3 py-2 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </header>

        <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero */}
          <motion.div
            initial={HERO_INITIAL}
            animate={HERO_ANIMATE}
            transition={HERO_TRANSITION}
            className="border border-zinc-800 bg-[#141414] p-6 md:p-10 mb-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-1 mb-5">
                  <Trophy className="w-3.5 h-3.5 text-red-600" strokeWidth={2.5} />
                  <span className="font-label text-zinc-300">White Belt to Black Belt Tracker</span>
                </div>
                <h1 className="font-heading text-5xl md:text-7xl leading-[0.9]">
                  Your journey,<br />
                  <span className="text-red-600">documented.</span>
                </h1>
                <p className="mt-5 text-zinc-400 max-w-xl leading-relaxed">
                  Log what you learned, schedule classes, set alerts, and watch your skill roadmap fill out one roll at a time.
                </p>
              </div>

              <div className="lg:min-w-[320px]">
                <p className="font-label mb-3">Current Belt</p>
                <div className="grid grid-cols-5 gap-1" data-testid="belt-selector">
                  {BELTS.map((b) => (
                    <button
                      key={b.key}
                      data-testid={`belt-${b.key.toLowerCase()}`}
                      onClick={async () => {
                        try {
                          await setProfile({ belt: b.key });
                        } catch (err) {
                          console.error("Belt update failed", err);
                          toast.error(err?.code === "permission-denied"
                            ? "Firestore rules not published yet — see FIREBASE_SETUP.md"
                            : `Couldn't update belt: ${err?.message || "unknown"}`);
                        }
                      }}
                      className={`relative border ${belt === b.key ? "border-red-600" : "border-zinc-800 hover:border-zinc-600"} p-3 transition-colors group`}
                    >
                      <div className={`h-8 ${b.bg} ${b.key === "White" ? "border border-zinc-300" : ""} mb-2`}>
                        <div className="h-full w-2 bg-red-700 ml-auto" />
                      </div>
                      <p className={`font-heading text-xs ${belt === b.key ? "text-white" : "text-zinc-400"}`}>{b.key}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard testid="stat-entries" icon={<NotebookPen strokeWidth={2.5} />} label="Journal Entries" value={notes.length} accent={currentBelt.accent} />
            <StatCard testid="stat-skills" icon={<Target strokeWidth={2.5} />} label="Skills Logged" value={completedSkills} accent={currentBelt.accent} />
            <StatCard testid="stat-events" icon={<CalendarDays strokeWidth={2.5} />} label="Upcoming Sessions" value={events.length} accent={currentBelt.accent} />
            <StatCard testid="stat-progress" icon={<Dumbbell strokeWidth={2.5} />} label="Roadmap Progress" value={`${progress}%`} accent={currentBelt.accent} />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Notebook */}
            <section className="tactical-card p-6 lg:col-span-2" data-testid="notebook-section">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
                <div>
                  <p className="font-label text-red-600">// 01</p>
                  <h2 className="font-heading text-3xl mt-1">Daily Training Notebook</h2>
                  <p className="text-zinc-500 text-sm mt-1">Techniques, mistakes, wins, and questions for your coach.</p>
                </div>
                <div className={`border border-zinc-800 px-4 py-2 ${currentBelt.bg} ${currentBelt.text} font-heading tracking-wider text-sm`}>
                  {belt} Belt
                </div>
              </div>

              <NoteForm newNote={newNote} setNewNote={setNewNote} onSubmit={addNote} skillSuggestions={allSkills.map((s) => s.name)} />

              <div className="flex items-center gap-3 border border-zinc-800 bg-[#0A0A0A] px-4 py-2.5 mt-6">
                <Search className="w-4 h-4 text-zinc-500" />
                <input
                  data-testid="search-input"
                  placeholder="Search journal by skill, detail, or title..."
                  value={query_}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-sm"
                />
              </div>

              <div className="mt-4 space-y-3" data-testid="notes-list">
                {filteredNotes.length === 0 && (
                  <div className="border border-dashed border-zinc-800 py-12 text-center">
                    <p className="font-label text-zinc-600">No entries yet — log your first roll above</p>
                  </div>
                )}
                {filteredNotes.map((note) => (
                  <motion.div
                    key={note.id}
                    initial={ITEM_INITIAL}
                    animate={ITEM_ANIMATE}
                    className="border border-zinc-800 bg-[#0A0A0A] p-4 hover:border-zinc-700 transition-colors"
                    data-testid={`note-item-${note.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-red-600">{note.date}</p>
                        <h3 className="font-heading text-xl mt-1 break-words">{note.title}</h3>
                        <p className="mt-2 text-sm text-zinc-400 leading-relaxed break-words">{note.details}</p>
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                          <span className="border border-zinc-800 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                            {note.skill || "General"}
                          </span>
                          <span className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Square key={n} className="w-2.5 h-2.5" strokeWidth={2}
                                fill={n <= note.rating ? "#DC2626" : "transparent"}
                                color={n <= note.rating ? "#DC2626" : "#3F3F46"} />
                            ))}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => notesCol.remove(note.id)}
                        data-testid={`delete-note-${note.id}`}
                        className="text-zinc-500 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <aside className="space-y-6">
              <section className="tactical-card p-6" data-testid="calendar-section">
                <p className="font-label text-red-600">// 02</p>
                <h2 className="font-heading text-3xl mt-1 flex items-center gap-2">
                  <CalendarDays className="w-6 h-6" strokeWidth={2.5} /> Calendar & Alerts
                </h2>
                <p className="text-zinc-500 text-sm mt-1">Schedule classes, open mats, tournaments.</p>

                <EventForm newEvent={newEvent} setNewEvent={setNewEvent} onSubmit={addEvent} />

                <div className="mt-5 space-y-3" data-testid="events-list">
                  {events.length === 0 && (
                    <div className="border border-dashed border-zinc-800 py-8 text-center">
                      <p className="font-label text-zinc-600">No sessions scheduled</p>
                    </div>
                  )}
                  {events.map((ev) => (
                    <div key={ev.id} className="border border-zinc-800 bg-[#0A0A0A] p-4" data-testid={`event-item-${ev.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-lg break-words">{ev.title}</h3>
                          <p className="mt-1 flex items-center gap-2 text-xs text-zinc-400 font-mono">
                            <Clock className="w-3 h-3" /> {ev.date} • {ev.time}
                          </p>
                          <p className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                            <Bell className="w-3 h-3" /> {ev.alert}
                          </p>
                        </div>
                        <button
                          onClick={() => eventsCol.remove(ev.id)}
                          data-testid={`delete-event-${ev.id}`}
                          className="text-zinc-500 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => sendReminder(ev)}
                        disabled={sendingId === ev.id}
                        data-testid={`send-reminder-${ev.id}`}
                        className="mt-3 w-full border border-zinc-700 hover:border-red-600 hover:text-red-500 disabled:opacity-50 px-3 py-2 text-xs font-heading tracking-wider flex items-center justify-center gap-2 transition-colors"
                      >
                        <Mail className="w-3.5 h-3.5" strokeWidth={2.5} />
                        {sendingId === ev.id ? "Sending..." : "Email Me This Reminder"}
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <SkillRoadmap
                notes={notes}
                belt={belt}
                currentBelt={currentBelt}
                onOpenPromotion={() => setPromoOpen(true)}
                onAddCustomSkill={(skill) => customSkillsCol.add(skill)}
                onRemoveCustomSkill={(id) => customSkillsCol.remove(id)}
                allSkills={allSkills}
                completedSkills={completedSkills}
                progress={progress}
              />
            </aside>
          </div>

          <div className="mt-6">
            <ActivityHeatmap notes={notes} events={events} />
          </div>

          <div className="mt-6">
            <TournamentSection belt={belt} user={user} />
          </div>

          <footer className="mt-12 border-t border-zinc-800 pt-6 pb-8 flex items-center justify-between">
            <p className="font-mono text-xs text-zinc-600">TATAMI LOG // TRAIN HARD. TAP OFTEN. LEARN ALWAYS.</p>
            <p className="font-mono text-xs text-zinc-600">v2.0 · Firebase</p>
          </footer>
        </main>

        <PromotionCard
          open={promoOpen}
          onClose={() => setPromoOpen(false)}
          belt={belt}
          userName={user?.displayName || user?.email?.split("@")[0]}
          skills={loggedSkillNames.length ? loggedSkillNames : allSkills.map((s) => s.name)}
        />
      </div>
    </TooltipProvider>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div className={`tactical-card border-t-4 ${accent} p-5`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-label">{label}</p>
          <p className="font-heading text-5xl mt-2 leading-none">{value}</p>
        </div>
        <div className="border border-zinc-800 p-2 text-red-600">{icon}</div>
      </div>
    </div>
  );
}
